import type { GridColDef } from '@mui/x-data-grid';
import { formatPrice } from '@vy/services';
import {
  DEFAULT_CURRENCY,
  EMAIL_VERIFICATION_SOURCE,
  type FailedInvoiceDeliveriesReportItem,
} from '@vy/types';
import { isEmptyString } from '@vy/utils';
import type { FieldPath } from 'react-hook-form';
import { VyTableCell } from '../../../../components/new-design/table/vy-table/VyTableCell';
import { compareDatesDescending, formatDate } from '../../../../utils/date';

export const FAILED_INVOICE_DELIVERIES_REPORT_KEYS = Object.freeze({
  DATE: 'date',
  CUSTOMER_NAME: 'customer.name',
  INVOICE_NUMBER: 'invoice.invoiceNumber',
  ISSUED_AT: 'invoice.issuedAt',
  AMOUNT: 'invoice.revenueBreakdown.total',
  REASON: 'reason',
  RECIPIENT: 'recipient',
} satisfies Record<string, FieldPath<FailedInvoiceDeliveriesReportItem>>);

export const FAILED_INVOICE_DELIVERIES_REPORT_COLUMNS: GridColDef<FailedInvoiceDeliveriesReportItem>[] = [
  {
    field: FAILED_INVOICE_DELIVERIES_REPORT_KEYS.DATE,
    headerName: 'Sent date',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 100,
    headerAlign: 'left',
    align: 'left',
    valueGetter: (_value, row) => row.date,
    valueFormatter: value => formatDate(value),
  },
  {
    field: FAILED_INVOICE_DELIVERIES_REPORT_KEYS.CUSTOMER_NAME,
    headerName: 'Customer',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 75,
    headerAlign: 'left',
    align: 'left',
    valueGetter: (_value, row) => row.customer.name,
    renderCell: ({ row }) => <VyTableCell value={row.customer.name} />,
  },
  {
    field: FAILED_INVOICE_DELIVERIES_REPORT_KEYS.INVOICE_NUMBER,
    headerName: 'Invoice',
    headerAlign: 'left',
    align: 'left',
    width: 90,
    valueGetter: (_value, row) => row.invoice.invoiceNumber,
    renderCell: ({ row }) => <VyTableCell value={row.invoice.invoiceNumber} />,
  },
  {
    field: FAILED_INVOICE_DELIVERIES_REPORT_KEYS.ISSUED_AT,
    headerName: 'Issued At',
    headerAlign: 'left',
    align: 'left',
    width: 100,
    valueGetter: (_value, row) => row.invoice.issuedAt ?? row.invoice.invoicingDate,
    valueFormatter: value => formatDate(value),
    sortComparator: (issuedAt1, issuedAt2) => compareDatesDescending(issuedAt1, issuedAt2),
  },
  {
    field: FAILED_INVOICE_DELIVERIES_REPORT_KEYS.AMOUNT,
    headerName: 'Invoice Amount',
    headerAlign: 'left',
    align: 'left',
    minWidth: 150,
    flex: 1,
    valueGetter: (_value, row) => row.invoice.revenueBreakdown.total,
    renderCell: ({ row }) => (
      <VyTableCell
        value={formatPrice(row.invoice.revenueBreakdown.total, row.customer?.currency ?? DEFAULT_CURRENCY)}
      />
    ),
  },
  {
    field: FAILED_INVOICE_DELIVERIES_REPORT_KEYS.RECIPIENT,
    headerName: 'Recipient',
    headerAlign: 'left',
    align: 'left',
    minWidth: 150,
    flex: 1,
    valueGetter: (_value, row) => row.recipient,
    renderCell: ({ row }) => <VyTableCell value={isEmptyString(row.recipient) ? '--' : row.recipient} />,
  },
  {
    field: FAILED_INVOICE_DELIVERIES_REPORT_KEYS.REASON,
    headerName: 'Reason',
    headerAlign: 'left',
    align: 'left',
    minWidth: 300,
    flex: 2,
    valueGetter: (_value, row) => row.source === EMAIL_VERIFICATION_SOURCE.Vayu ? row.reason : 'Email could not be delivered',
    renderCell: ({ row }) => (
      <VyTableCell
        value={row.source === EMAIL_VERIFICATION_SOURCE.Vayu ? row.reason : 'Email could not be delivered'}
        containerClassName="justify-start"
      />
    ),
  },
];
